/**
 * Leave Type form — create, edit, and detail (read-only). Same pattern as OTRuleForm.
 */

import type {
  CreateLeaveTypeDTO,
  LeaveTypeDetailDTO,
  UpdateLeaveTypeDTO,
} from "@/modules/hr/leave/domain/types";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormNumber,
  FormRoot,
  FormTextarea,
  Switch,
  toast,
  zodResolver,
} from "@devhop/ui";
import { CalendarClock, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import * as RHF from "react-hook-form";
import { z } from "zod";
import { leaveTypeFormSamples } from "./leaveTypeFormSamples";

/** FormNumber ສົ່ງເລກ; ຟິວວ່າງ = ບໍ່ຈຳກັດ (undefined → null ໃນ DTO) */
const optionalMaxDaysPerYearSchema = z
  .union([z.string(), z.number()])
  .transform((val) => {
    if (val === "" || val === null || val === undefined) return undefined;
    if (typeof val === "number") {
      if (Number.isNaN(val)) return undefined;
      return val;
    }
    const s = val.trim();
    if (!s) return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  })
  .pipe(
    z
      .number()
      .int("ຕ້ອງເປັນຈຳນວນເຕັມ")
      .min(0, "ບໍ່ຕ່ຳກວ່າ 0")
      .optional(),
  );

const LeaveTypeFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາປ້ອນຊື່ປະເພດລາ"),
  code: z.string().min(1, "ກະລຸນາປ້ອນລະຫັດ"),
  description: z.string(),
  isPaid: z.boolean(),
  maxDaysPerYear: optionalMaxDaysPerYearSchema,
  allowCarryOver: z.boolean(),
  isActive: z.boolean(),
  orderIndex: z.number(),
});

export type LeaveTypeFormValues = {
  name: string;
  code: string;
  description: string;
  isPaid: boolean;
  maxDaysPerYear?: number;
  allowCarryOver: boolean;
  isActive: boolean;
  orderIndex: number;
};

export type LeaveTypeFormProps =
  | {
      mode: "create";
      type?: LeaveTypeDetailDTO | null;
      onSubmit: (values: CreateLeaveTypeDTO) => void;
      onCancel: () => void;
      isSubmitting?: boolean;
    }
  | {
      mode: "edit";
      type?: LeaveTypeDetailDTO | null;
      onSubmit: (values: UpdateLeaveTypeDTO) => void;
      onCancel: () => void;
      isSubmitting?: boolean;
    }
  | {
      mode: "detail";
      type: LeaveTypeDetailDTO;
      onCancel: () => void;
      onEdit?: () => void;
      isSubmitting?: never;
    };

export function LeaveTypeForm(props: LeaveTypeFormProps) {
  const { mode, type } = props;
  const isCreate = mode === "create";
  const isDetail = mode === "detail";

  const defaultValues: LeaveTypeFormValues = useMemo(() => {
    if (type) {
      return {
        name: type.name,
        code: type.code,
        description: type.description ?? "",
        isPaid: type.isPaid,
        maxDaysPerYear:
          type.maxDaysPerYear != null ? type.maxDaysPerYear : undefined,
        allowCarryOver: type.allowCarryOver,
        isActive: type.isActive,
        orderIndex: type.orderIndex,
      };
    }
    return {
      name: "",
      code: "",
      description: "",
      isPaid: true,
      maxDaysPerYear: undefined,
      allowCarryOver: false,
      isActive: true,
      orderIndex: 0,
    };
  }, [type]);

  const [sampleIndex, setSampleIndex] = useState(0);
  const isDev =
    typeof import.meta !== "undefined" &&
    (import.meta.hot !== undefined || import.meta.env?.MODE === "development");

  const methods = RHF.useForm<LeaveTypeFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(LeaveTypeFormSchema) as any,
    defaultValues,
    values:
      type && !isCreate
        ? {
            name: type.name,
            code: type.code,
            description: type.description ?? "",
            isPaid: type.isPaid,
            maxDaysPerYear:
              type.maxDaysPerYear != null ? type.maxDaysPerYear : undefined,
            allowCarryOver: type.allowCarryOver,
            isActive: type.isActive,
            orderIndex: type.orderIndex,
          }
        : undefined,
  });

  const handleAutoFill = () => {
    const sample = leaveTypeFormSamples[sampleIndex];
    if (sample) {
      methods.setValue("name", sample.name);
      methods.setValue("code", sample.code);
      methods.setValue("description", sample.description);
      methods.setValue("isPaid", sample.isPaid);
      methods.setValue("maxDaysPerYear", sample.maxDaysPerYear);
      methods.setValue("allowCarryOver", sample.allowCarryOver);
      methods.setValue("isActive", sample.isActive);
      methods.setValue("orderIndex", sample.orderIndex);
      setSampleIndex((prev) => (prev + 1) % leaveTypeFormSamples.length);
      toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ. ກະລຸນາແກ້ໄຂຕາມຄວາມເໝາະສົມ.");
    }
  };

  const handleValidSubmit = (vals: LeaveTypeFormValues) => {
    const dto = {
      name: vals.name.trim(),
      code: vals.code.trim(),
      description: vals.description.trim() || null,
      isPaid: vals.isPaid,
      maxDaysPerYear: vals.maxDaysPerYear ?? null,
      allowCarryOver: vals.allowCarryOver,
      isActive: vals.isActive,
      orderIndex: vals.orderIndex,
    };
    if (isCreate) {
      (props as { onSubmit: (v: CreateLeaveTypeDTO) => void }).onSubmit(
        dto as CreateLeaveTypeDTO,
      );
    } else {
      (props as { onSubmit: (v: UpdateLeaveTypeDTO) => void }).onSubmit(
        dto as UpdateLeaveTypeDTO,
      );
    }
  };

  if (isDetail && type) {
    const t = type;
    return (
      <Card className="w-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-4 w-4" />
            {t.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          <DetailRow label="ຊື່" value={t.name} />
          <DetailRow label="ລະຫັດ" value={t.code} />
          <DetailRow label="ຄຳອະທິບາຍ" value={t.description} />
          <DetailRow
            label="ລາຮັບເງິນ"
            value={t.isPaid ? "ຄິດເງິນເດືອນ" : "ບໍ່ຄິດເງິນເດືອນ"}
          />
          <DetailRow
            label="ວັນສູງສຸດຕໍ່ປີ"
            value={t.maxDaysPerYear != null ? String(t.maxDaysPerYear) : "—"}
          />
          <DetailRow
            label="ນຳວັນຄົງເຫຼືອໄປໃຊ້ປີຖັດ"
            value={t.allowCarryOver ? "ອະນຸຍາດ" : "ບໍ່ອະນຸຍາດ"}
          />
          <DetailRow label="ເປີດໃຊ້ງານ" value={t.isActive ? "ໃຊ້ງານຢູ່" : "ປິດແລ້ວ"} />
          <DetailRow label="ລຳດັບການສະແດງຜົນ" value={String(t.orderIndex)} />
        </CardContent>
        <div className="flex flex-wrap items-center justify-between gap-4 border-t px-6 py-4">
          <Button variant="outline" onClick={props.onCancel} type="button">
            ກັບຄືນ
          </Button>
          {props.onEdit && (
            <Button onClick={props.onEdit} type="button">
              ແກ້ໄຂ
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div className="mx-auto">
      <FormRoot<LeaveTypeFormValues>
        methods={methods}
        onSubmit={handleValidSubmit}
        className="w-full"
      >
        <Card className="w-full">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4" />
              {isCreate ? "ສ້າງປະເພດການລາ" : "ແກ້ໄຂປະເພດການລາ"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormInput
              name="name"
              label="ຊື່"
              placeholder="ຕົວຢ່າງ: ລາພັກ"
              requiredMark
              disabled={isDetail}
            />
            <FormInput
              name="code"
              label="ລະຫັດ"
              placeholder="ຕົວຢ່າງ: ANNUAL"
              requiredMark
              disabled={isDetail}
            />
            <FormTextarea
              name="description"
              label="ຄຳອະທິບາຍ"
              rows={2}
              placeholder="ອະທິບາຍເພີ່ມເຕີມ..."
              hint="ບໍ່ບັງຄັບ"
              disabled={isDetail}
            />
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium text-sm">ລາຮັບເງິນ</p>
                <p className="text-muted-foreground text-xs">
                  ປະເພດລານີ້ຄິດເງິນເດືອນໃນວັນລາຫຼືບໍ່
                </p>
              </div>
              <Switch
                id="leave-type-is-paid"
                checked={methods.watch("isPaid")}
                onCheckedChange={(checked) =>
                  methods.setValue("isPaid", checked)
                }
                disabled={isDetail}
              />
            </div>
            <FormNumber
              name="maxDaysPerYear"
              label="ວັນສູງສຸດຕໍ່ປີ"
              placeholder="ວ່າງ = ບໍ່ຈຳກັດ"
              hint="ຈຳນວນວັນລາສູງສຸດທີ່ໃຊ້ໄດ້ຕໍ່ປີ. ວ່າງ = ບໍ່ຈຳກັດ"
              disabled={isDetail}
            />
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium text-sm">ນຳວັນຄົງເຫຼືອໄປໃຊ້ປີຖັດ</p>
                <p className="text-muted-foreground text-xs">
                  ຖ້າເປີດ: ວັນລາທີ່ໃຊ້ບໍ່ຄົບໃນປີນີ້ ສາມາດນຳໄປໃຊ້ຕໍ່ໃນປີຖັດໄດ້
                </p>
              </div>
              <Switch
                id="leave-type-allow-carry-over"
                checked={methods.watch("allowCarryOver")}
                onCheckedChange={(checked) =>
                  methods.setValue("allowCarryOver", checked)
                }
                disabled={isDetail}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium text-sm">ເປີດໃຊ້ງານ</p>
                <p className="text-muted-foreground text-xs">
                  ປະເພດລານີ້ສາມາດໃຊ້ສະໝັກລາໄດ້ຢູ່ ຫຼື ປິດແລ້ວ
                </p>
              </div>
              <Switch
                id="leave-type-is-active"
                checked={methods.watch("isActive")}
                onCheckedChange={(checked) =>
                  methods.setValue("isActive", checked)
                }
                disabled={isDetail}
              />
            </div>
            <FormNumber
              name="orderIndex"
              label="ລຳດັບການສະແດງຜົນ"
              placeholder="0"
              hint="ຕົວເລກນ້ອຍ = ສະແດງກ່ອນ"
              disabled={isDetail}
            />
          </CardContent>
        </Card>
        <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={props.onCancel}
              disabled={props.mode !== "detail" ? props.isSubmitting : false}
            >
              ຍົກເລີກ
            </Button>
            {isDev && isCreate && leaveTypeFormSamples.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAutoFill}
                disabled={props.isSubmitting}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                ຕື່ມຕົວຢ່າງ ({sampleIndex + 1}/{leaveTypeFormSamples.length})
              </Button>
            )}
          </div>
          <Button
            type="submit"
            disabled={props.mode !== "detail" ? props.isSubmitting : false}
            isLoading={props.mode !== "detail" ? props.isSubmitting : false}
          >
            {isCreate ? "ສ້າງ" : "ບັນທຶກ"}
          </Button>
        </div>
      </FormRoot>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 border-border border-b py-2 last:border-0">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-sm">{value ?? "—"}</span>
    </div>
  );
}
